import { useEffect, useRef, useState, useCallback } from "react";
import {
  X, Camera, Upload, Zap, ZapOff, SwitchCamera, RotateCcw, RotateCw,
  Check, Loader2, ScanLine, Wand2, ScanSearch, Crop,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

// SEMUA pemrosesan berat (deteksi tepi, perspektif, penjernihan) berjalan DI SERVER.
// Perangkat tidak mengunduh mesin apa pun. Deteksi LIVE memakai frame kecil (~40KB) yang
// dikirim berkala ke server, lalu AUTO-FOTO saat sudut terkunci stabil.
const MODES = [
  { id: "original", label: "Original" },
  { id: "color", label: "Warna Dok" },
  { id: "gray", label: "Grayscale" },
  { id: "bw", label: "Hitam Putih" },
];

const DEFAULT_OPTS = { mode: "color", brightness: 0, contrast: 1, sharpness: 0.6 };
const MAX_SIDE = 3500; // sisi terpanjang yang dikirim ke server (resolusi penuh utk akurasi NIK & cetak A4/F4)
const LIVE_SIDE = 500;  // ukuran frame deteksi live (kecil = cepat & hemat kuota)
const AUTO_CONF = 0.62;  // keyakinan minimum server sebelum boleh mulai mengunci auto-foto
const AUTO_FRAMES = 4;   // jumlah frame stabil+yakin berturut sebelum auto-foto (~2.8 dtk)

// ---------- helpers ----------
function downscaleToCanvas(source, maxSide = MAX_SIDE) {
  const nw = source.videoWidth || source.naturalWidth || source.width;
  const nh = source.videoHeight || source.naturalHeight || source.height;
  const scale = Math.min(1, maxSide / Math.max(nw, nh));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(nw * scale));
  c.height = Math.max(1, Math.round(nh * scale));
  c.getContext("2d").drawImage(source, 0, 0, c.width, c.height);
  return c;
}

function canvasToBlob(canvas, quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Gagal mengenkode gambar"))), "image/jpeg", quality);
  });
}

// Capture LOSSLESS (PNG) — tanpa kompresi, hasil tetap jernih seperti asli.
function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Gagal mengenkode gambar"))), "image/png");
  });
}

function blobToPreview(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      const fr = new FileReader();
      fr.onload = () => { URL.revokeObjectURL(url); resolve({ dataUrl: fr.result, ...dims }); };
      fr.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Gagal membaca hasil server")); };
      fr.readAsDataURL(blob);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Hasil server bukan gambar valid")); };
    img.src = url;
  });
}

function quadMetrics(c) {
  const cx = (c[0].x + c[1].x + c[2].x + c[3].x) / 4;
  const cy = (c[0].y + c[1].y + c[2].y + c[3].y) / 4;
  let a = 0;
  for (let i = 0; i < 4; i += 1) {
    const j = (i + 1) % 4;
    a += c[i].x * c[j].y - c[j].x * c[i].y;
  }
  return { cx, cy, a: Math.abs(a) / 2 };
}

// Map normalized (0..1) frame corners to on-screen pixels for an object-cover video.
function coverPoints(corners, vw, vh) {
  const elW = window.innerWidth;
  const elH = window.innerHeight;
  if (!vw || !vh) return null;
  const scale = Math.max(elW / vw, elH / vh);
  const dW = vw * scale;
  const dH = vh * scale;
  const ox = (elW - dW) / 2;
  const oy = (elH - dH) / 2;
  return corners.map((p) => ({ x: p.x * vw * scale + ox, y: p.y * vh * scale + oy }));
}

export default function SmartScanner({ onComplete, onClose, singlePage = false, orientation = "", paperSize = "A4" }) {
  const [source, setSource] = useState("camera");
  const [step, setStep] = useState("camera"); // camera | edit | enhance
  const [busy, setBusy] = useState("");
  const [detecting, setDetecting] = useState(false);

  // camera
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [vdim, setVdim] = useState({ w: 0, h: 0 });
  const fileRef = useRef(null);

  // live detection / auto-capture
  const [autoCap, setAutoCap] = useState(true);
  const [liveCorners, setLiveCorners] = useState(null); // normalized 0..1
  const [liveConf, setLiveConf] = useState(0); // keyakinan deteksi 0..1
  const [locking, setLocking] = useState(0); // 0..AUTO_FRAMES progress menuju auto-foto
  const inflightRef = useRef(false);
  const stableRef = useRef({ prev: null, count: 0 });
  const capturedRef = useRef(false);

  // edit
  const capRef = useRef(null);
  const scanIdRef = useRef(null);
  const [corners, setCorners] = useState(null); // koordinat gambar penuh
  const [view, setView] = useState({ w: 0, h: 0, scale: 1 });
  const dragRef = useRef(-1);
  const editWrapRef = useRef(null);

  // enhance
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [preview, setPreview] = useState(null);
  const lastSentRef = useRef("");
  const seqRef = useRef(0);

  // pages
  const [pages, setPages] = useState([]);

  // ---------- Camera lifecycle ----------
  const stopCamera = useCallback(() => {
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    setTorchOn(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Browser tidak mendukung kamera. Gunakan Upload File.");
      setSource("upload");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 3840 }, height: { ideal: 2160 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      const track = stream.getVideoTracks?.()[0];
      let caps = {};
      try { caps = track?.getCapabilities ? track.getCapabilities() : {}; } catch (_) { caps = {}; }
      setTorchSupported(!!caps.torch);
    } catch (e) {
      toast.error("Tidak dapat mengakses kamera. Izinkan kamera atau gunakan Upload File.");
      setSource("upload");
    }
  }, [facing, stopCamera]);

  useEffect(() => {
    if (source === "camera" && step === "camera") startCamera();
    else stopCamera();
    return () => stopCamera();
     
  }, [source, step, facing]);

  // ---------- LIVE detection loop (server-assisted) ----------
  const handleLive = useCallback((cs, conf = 0) => {
    setLiveCorners(cs);
    setLiveConf(conf);
    if (!cs) { stableRef.current = { prev: null, count: 0 }; setLocking(0); return; }
    const m = quadMetrics(cs);
    const prev = stableRef.current.prev;
    // Hanya menghitung menuju auto-foto bila: geometri stabil, area cukup, DAN keyakinan tinggi.
    const steady = prev
      && Math.abs(m.cx - prev.cx) < 0.03
      && Math.abs(m.cy - prev.cy) < 0.03
      && Math.abs(m.a - prev.a) < 0.045
      && m.a > 0.12
      && conf >= AUTO_CONF;
    stableRef.current = { prev: m, count: steady ? stableRef.current.count + 1 : 0 };
    setLocking(Math.min(AUTO_FRAMES, stableRef.current.count));
    if (autoCap && steady && stableRef.current.count >= AUTO_FRAMES && !capturedRef.current) {
      capturedRef.current = true;
      captureNow(cs);
    }
     
  }, [autoCap]);

  useEffect(() => {
    if (!(source === "camera" && step === "camera")) return undefined;
    capturedRef.current = false;
    let cancelled = false;
    let timer = null;
    const loop = async () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (v && v.videoWidth && !inflightRef.current) {
        inflightRef.current = true;
        try {
          const small = downscaleToCanvas(v, LIVE_SIDE);
          const blob = await canvasToBlob(small, 0.5);
          const fd = new FormData();
          fd.append("file", new File([blob], "f.jpg", { type: "image/jpeg" }));
          const { data } = await api.post("/scan/detect", fd, { timeout: 8000 });
          if (!cancelled) handleLive(data.corners || null, data.confidence || 0);
        } catch (_) { /* abaikan error deteksi live */ }
        finally { inflightRef.current = false; }
      }
      if (!cancelled) timer = setTimeout(loop, 700);
    };
    timer = setTimeout(loop, 900);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      setLiveCorners(null);
      setLiveConf(0);
      setLocking(0);
      stableRef.current = { prev: null, count: 0 };
    };
     
  }, [source, step, handleLive]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch (_) {
      toast.error("Flash tidak didukung kamera ini");
    }
  };

  // ---------- Capture + kirim ke server ----------
  const processFullImage = async (cap, liveNorm) => {
    capRef.current = cap;
    scanIdRef.current = null;
    setStep("edit");
    setDetecting(true);
    // sudut awal: hasil live (dinormalisasi) bila ada, else margin default
    const mx = cap.width * 0.06;
    const my = cap.height * 0.06;
    let startCorners = liveNorm
      ? liveNorm.map((p) => ({ x: p.x * cap.width, y: p.y * cap.height }))
      : [
          { x: mx, y: my }, { x: cap.width - mx, y: my },
          { x: cap.width - mx, y: cap.height - my }, { x: mx, y: cap.height - my },
        ];
    setCorners(startCorners);
    try {
      const blob = await canvasToPngBlob(cap);
      const fd = new FormData();
      fd.append("file", new File([blob], "scan.png", { type: "image/png" }));
      const { data } = await api.post("/scan/prepare", fd, { timeout: 120000 });
      scanIdRef.current = data.scan_id;
      const serverCorners = data.corners && data.corners.length === 4 ? data.corners : null;
      if (serverCorners) startCorners = serverCorners;
      setCorners(startCorners);
      setDetecting(false);
      // JANGAN auto-warp. Tetap di langkah "edit" agar pengguna MEMASTIKAN 4 sudut pas ke
      // tepi dokumen dulu (mencegah hasil "ketekuk"/terpotong lebih pendek), lalu tekan "Proses".
      // (Seperti Adobe/CamScanner: auto-deteksi sudut, tapi crop dikonfirmasi pengguna.)
    } catch (e) {
      setDetecting(false);
      toast.error(`${apiError(e)} — atur sudut manual lalu tekan Proses`);
    }
  };

  const captureNow = (liveNorm) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setLocking(0);
    processFullImage(downscaleToCanvas(v), liveNorm || null);
  };

  const manualCapture = () => { capturedRef.current = true; captureNow(liveCorners); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(img.src); processFullImage(downscaleToCanvas(img), null); };
    img.onerror = () => toast.error("Gagal membuka gambar");
    img.src = URL.createObjectURL(file);
  };

  // ---------- Edit view sizing ----------
  useEffect(() => {
    if (step !== "edit" || !capRef.current) return;
    const cap = capRef.current;
    const maxW = Math.min(window.innerWidth - 24, 900);
    const maxH = window.innerHeight - 200;
    let scale = Math.min(maxW / cap.width, maxH / cap.height, 1);
    if (!isFinite(scale) || scale <= 0) scale = 1;
    setView({ w: cap.width * scale, h: cap.height * scale, scale });
  }, [step]);

  const onPointerMove = (e) => {
    if (dragRef.current < 0 || !editWrapRef.current) return;
    const rect = editWrapRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / view.scale;
    const y = (e.clientY - rect.top) / view.scale;
    const cap = capRef.current;
    setCorners((prev) => prev.map((p, i) => (i === dragRef.current
      ? { x: Math.max(0, Math.min(cap.width, x)), y: Math.max(0, Math.min(cap.height, y)) } : p)));
  };

  // ---------- Proses di server (+ auto-rotate) ----------
  const doPerspective = async (cornersArg) => {
    const cs = cornersArg || corners;
    if (!scanIdRef.current) { toast.error("Foto belum terkirim ke server. Ulangi."); return; }
    setBusy("Memproses di server...");
    try {
      const o = { ...DEFAULT_OPTS };
      const res = await api.post("/scan/process", { scan_id: scanIdRef.current, corners: cs, paper_size: paperSize, ...o }, { responseType: "blob", timeout: 120000 });
      setOpts(o);
      lastSentRef.current = JSON.stringify(o);
      seqRef.current += 1;
      let pv = await blobToPreview(res.data);
      // AUTO-ROTATE mengikuti orientasi kertas yang dipilih
      if (orientation) {
        const wantPortrait = orientation === "portrait";
        const isPortrait = pv.height >= pv.width;
        if (wantPortrait !== isPortrait) {
          const r2 = await api.post("/scan/enhance", { scan_id: scanIdRef.current, ...o, rot: 90 }, { responseType: "blob", timeout: 120000 });
          pv = await blobToPreview(r2.data);
        }
      }
      setPreview(pv);
      setStep("enhance");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (step !== "enhance" || !scanIdRef.current) return undefined;
    const key = JSON.stringify(opts);
    if (key === lastSentRef.current) return undefined;
    const seq = ++seqRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/scan/enhance", { scan_id: scanIdRef.current, ...opts, rot: 0 }, { responseType: "blob", timeout: 120000 });
        if (seq !== seqRef.current) return;
        lastSentRef.current = key;
        setPreview(await blobToPreview(res.data));
      } catch (e) { toast.error(apiError(e)); }
    }, 400);
    return () => clearTimeout(t);
     
  }, [opts, step]);

  const autoEnhance = () => setOpts({ mode: "color", brightness: 6, contrast: 1.15, sharpness: 0.9 });

  const rotatePage = async (deg) => {
    if (!scanIdRef.current) return;
    setBusy("Memutar...");
    try {
      seqRef.current += 1;
      const res = await api.post("/scan/enhance", { scan_id: scanIdRef.current, ...opts, rot: deg }, { responseType: "blob", timeout: 120000 });
      lastSentRef.current = JSON.stringify(opts);
      setPreview(await blobToPreview(res.data));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const resetToCamera = () => {
    scanIdRef.current = null;
    setPreview(null);
    setCorners(null);
    lastSentRef.current = "";
    capturedRef.current = false;
    setStep("camera");
  };

  const savePage = () => {
    if (!preview) return;
    const page = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...preview };
    if (singlePage) { cleanupAndComplete([page]); return; }
    setPages((p) => [...p, page]);
    toast.success("Halaman tersimpan. Ambil halaman lain atau tekan Selesai.");
    resetToCamera();
  };

  const cleanupAndComplete = (finalPages) => { stopCamera(); onComplete(finalPages); };
  const finishAll = () => {
    if (pages.length === 0) { toast.error("Belum ada halaman"); return; }
    cleanupAndComplete(pages);
  };

  // ---------- Render ----------
  return (
    <Shell onClose={() => { stopCamera(); onClose(); }} title={stepTitle(step)} hideHeader={step === "camera"}>
      {step === "camera" && (
        <div className="absolute inset-0 bg-black" data-testid="scanner-camera-full">
          {source === "camera" ? (
            <>
              <video
                ref={videoRef}
                playsInline muted autoPlay
                onLoadedMetadata={() => { const v = videoRef.current; if (v) setVdim({ w: v.videoWidth, h: v.videoHeight }); }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Overlay deteksi kertas (dipetakan ke object-cover) */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`} preserveAspectRatio="none">
                {liveCorners && vdim.w > 0 && (() => {
                  const pts = coverPoints(liveCorners, vdim.w, vdim.h);
                  const confident = liveConf >= AUTO_CONF;
                  const stroke = confident ? "#10b981" : "#f59e0b";
                  const fill = locking >= AUTO_FRAMES
                    ? "rgba(16,185,129,0.28)"
                    : confident ? "rgba(16,185,129,0.14)" : "rgba(245,158,11,0.12)";
                  return pts ? (
                    <polygon
                      points={pts.map((p) => `${p.x},${p.y}`).join(" ")}
                      fill={fill}
                      stroke={stroke} strokeWidth="3"
                    />
                  ) : null;
                })()}
              </svg>
              {!liveCorners && (
                <div className="pointer-events-none absolute inset-6 border-2 border-dashed border-white/40 rounded-2xl" />
              )}
            </>
          ) : (
            <div className="absolute inset-0 grid place-items-center text-white/70">
              <button onClick={() => fileRef.current?.click()} className="px-5 py-3 rounded-xl bg-white/10 flex items-center gap-2" data-testid="scanner-pick-file">
                <Upload size={18} /> Pilih gambar dokumen
              </button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="scanner-file-input" />

          {/* ---- TOP floating bar ---- */}
          <div className="absolute top-0 inset-x-0 flex items-center justify-between p-3 bg-gradient-to-b from-black/60 to-transparent" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
            <button onClick={() => { stopCamera(); onClose(); }} className="h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white" data-testid="scanner-close"><X size={20} /></button>
            <div className="text-white text-xs bg-black/45 px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="scanner-live-status">
              {locking >= AUTO_FRAMES ? (<><Check size={13} className="text-emerald-400" /> Terkunci — mengambil...</>)
                : liveCorners && liveConf >= AUTO_CONF ? (<><ScanSearch size={13} className="text-emerald-400 animate-pulse" /> Kertas terdeteksi{autoCap ? ` — tahan stabil (${locking}/${AUTO_FRAMES})` : ""}</>)
                : liveCorners ? (<><ScanSearch size={13} className="text-amber-400" /> Perjelas kertas — dekatkan & tahan stabil</>)
                : (<><ScanLine size={13} /> Arahkan ke seluruh kertas</>)}
            </div>
            <button onClick={() => { setSource((s) => (s === "camera" ? "upload" : "camera")); if (source === "camera") fileRef.current?.click(); }}
              className="h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white" title="Upload File" data-testid="scanner-source-toggle"><Upload size={18} /></button>
          </div>

          {/* ---- pages tray (multi-halaman) ---- */}
          {!singlePage && pages.length > 0 && (
            <div className="absolute left-0 right-0 bottom-32 flex gap-2 overflow-x-auto px-3 py-1">
              {pages.map((pg, i) => (
                <div key={pg.id} className="relative shrink-0">
                  <img src={pg.dataUrl} alt={`hal ${i + 1}`} className="h-14 w-11 object-cover rounded border border-white/40" />
                  <span className="absolute -top-1 -left-1 bg-emerald-600 text-white text-[10px] h-4 w-4 grid place-items-center rounded-full">{i + 1}</span>
                </div>
              ))}
            </div>
          )}

          {/* ---- BOTTOM floating controls ---- */}
          {source === "camera" && (
            <div className="absolute bottom-0 inset-x-0 pt-10 bg-gradient-to-t from-black/70 to-transparent" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
              <label className="flex items-center justify-center gap-2 text-white/90 text-xs mb-4">
                <input type="checkbox" checked={autoCap} onChange={(e) => setAutoCap(e.target.checked)} data-testid="scanner-autocap-toggle" className="accent-emerald-500" />
                Auto-foto saat kertas terkunci
              </label>
              <div className="flex items-center justify-around max-w-md mx-auto px-6">
                <IconBtn onClick={toggleTorch} disabled={!torchSupported} label="Flash" active={torchOn} testid="scanner-flash">
                  {torchOn ? <Zap size={22} /> : <ZapOff size={22} />}
                </IconBtn>
                <button onClick={manualCapture} data-testid="scanner-manual-capture" className="h-[74px] w-[74px] rounded-full bg-white grid place-items-center ring-4 ring-white/40 active:scale-95 transition">
                  <span className="h-[58px] w-[58px] rounded-full bg-[#064E3B] grid place-items-center text-white"><Camera size={26} /></span>
                </button>
                <IconBtn onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} label="Kamera" testid="scanner-switch">
                  <SwitchCamera size={22} />
                </IconBtn>
              </div>
              {!singlePage && pages.length > 0 && (
                <div className="px-6 mt-4">
                  <button onClick={finishAll} data-testid="scanner-finish"
                    className="w-full h-12 rounded-xl bg-[#D97706] text-white font-semibold flex items-center justify-center gap-2">
                    <Check size={18} /> Selesai ({pages.length} halaman)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === "edit" && (
        <div className="flex-1 flex flex-col min-h-0">
          <p className="text-center text-white/80 text-sm py-2 flex items-center justify-center gap-2 shrink-0">
            {detecting ? (<><Loader2 size={14} className="animate-spin" /> Mendeteksi tepi dokumen...</>)
              : "Seret titik sudut agar pas ke tepi dokumen"}
          </p>
          <div className="flex-1 grid place-items-center overflow-auto p-3 min-h-0">
            <div ref={editWrapRef} className="relative touch-none" style={{ width: view.w, height: view.h }}
              onPointerMove={onPointerMove} onPointerUp={() => { dragRef.current = -1; }} onPointerLeave={() => { dragRef.current = -1; }}>
              {capRef.current && (
                <img src={capRef.current.toDataURL("image/jpeg", 0.9)} alt="captured" className="absolute inset-0 w-full h-full" draggable={false} />
              )}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${capRef.current?.width || 1} ${capRef.current?.height || 1}`} preserveAspectRatio="none">
                {corners && (
                  <polygon points={corners.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(16,185,129,0.15)" stroke="#10b981" strokeWidth={Math.max(2, (capRef.current?.width || 800) / 300)} />
                )}
              </svg>
              {corners && corners.map((p, i) => (
                <div key={i} onPointerDown={(e) => { e.preventDefault(); dragRef.current = i; }} data-testid={`scanner-corner-${i}`}
                  className="absolute h-7 w-7 -ml-3.5 -mt-3.5 rounded-full bg-white border-2 border-emerald-500 shadow touch-none"
                  style={{ left: p.x * view.scale, top: p.y * view.scale }} />
              ))}
            </div>
          </div>
          <div className="p-3 bg-black/70 flex gap-2 shrink-0" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <button onClick={resetToCamera} className="flex-1 h-12 rounded-xl bg-white/15 text-white flex items-center justify-center gap-2"><RotateCcw size={17} /> Ulangi</button>
            <button onClick={() => doPerspective()} disabled={!!busy || detecting} data-testid="scanner-process" className="flex-1 h-12 rounded-xl bg-[#064E3B] text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} {busy || "Proses"}
            </button>
          </div>
        </div>
      )}

      {step === "enhance" && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 grid place-items-center overflow-auto p-3 bg-neutral-900 relative min-h-0">
            {preview ? (
              <img src={preview.dataUrl} alt="hasil" className="max-h-full max-w-full shadow-lg" data-testid="scanner-preview-img" />
            ) : (<Loader2 className="animate-spin text-white" size={30} />)}
            {busy && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs text-white bg-black/60 px-3 py-1 rounded-full flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> {busy}
              </div>
            )}
          </div>
          {/* Panel kontrol: bisa di-scroll agar tidak terpotong di layar kecil */}
          <div className="bg-black/80 p-3 space-y-3 shrink-0 overflow-y-auto max-h-[46vh]" data-testid="scanner-enhance-panel" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <div className="flex gap-2">
              {MODES.map((m) => (
                <button key={m.id} onClick={() => setOpts((o) => ({ ...o, mode: m.id }))} data-testid={`scanner-mode-${m.id}`}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium ${opts.mode === m.id ? "bg-white text-[#064E3B]" : "bg-white/10 text-white"}`}>{m.label}</button>
              ))}
            </div>
            <Slider label="Brightness" min={-80} max={80} step={1} value={opts.brightness} onChange={(v) => setOpts((o) => ({ ...o, brightness: v }))} />
            <Slider label="Contrast" min={0.5} max={2} step={0.05} value={opts.contrast} onChange={(v) => setOpts((o) => ({ ...o, contrast: v }))} />
            <Slider label="Sharpness" min={0} max={2} step={0.1} value={opts.sharpness} onChange={(v) => setOpts((o) => ({ ...o, sharpness: v }))} />
            <div className="flex gap-2">
              <button onClick={autoEnhance} data-testid="scanner-auto-enhance" className="flex-1 py-2.5 rounded-lg bg-white/10 text-white text-sm flex items-center justify-center gap-1.5"><Wand2 size={16} /> Auto Enhance</button>
              <button onClick={() => setStep("edit")} className="py-2.5 px-3 rounded-lg bg-white/10 text-white text-sm flex items-center gap-1.5" data-testid="scanner-readjust"><Crop size={16} /> Sudut</button>
              <button onClick={() => rotatePage(-90)} disabled={!!busy} className="py-2.5 px-3 rounded-lg bg-white/10 text-white disabled:opacity-40"><RotateCcw size={16} /></button>
              <button onClick={() => rotatePage(90)} disabled={!!busy} className="py-2.5 px-3 rounded-lg bg-white/10 text-white disabled:opacity-40"><RotateCw size={16} /></button>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={resetToCamera} className="flex-1 h-11 rounded-xl bg-white/15 text-white" data-testid="scanner-retake">Foto Ulang</button>
              <button onClick={savePage} disabled={!preview || !!busy} data-testid="scanner-save-page" className="flex-[2] h-11 rounded-xl bg-[#D97706] text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-2">
                <Check size={18} /> {singlePage ? "Gunakan Hasil" : "Simpan Halaman"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function stepTitle(step) {
  if (step === "edit") return "Sesuaikan Sudut";
  if (step === "enhance") return "Jernihkan & Simpan";
  return "Smart Scanner";
}

function Shell({ children, onClose, title, hideHeader }) {
  return (
    <div
      className="fixed left-0 right-0 top-0 z-[100] bg-black flex flex-col h-screen"
      style={{ height: "100dvh" }}
      data-testid="smart-scanner"
    >
      {!hideHeader && (
        <div
          className="flex items-center justify-between px-4 py-3 text-white shrink-0"
          style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
        >
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="scanner-close"><X size={22} /></button>
          <span className="text-sm font-medium">{title}</span>
          <div className="w-9" />
        </div>
      )}
      {children}
    </div>
  );
}


function IconBtn({ children, onClick, label, active, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex flex-col items-center gap-1 text-[11px] ${disabled ? "text-white/30" : "text-white"}`}>
      <span className={`h-12 w-12 rounded-full grid place-items-center ${active ? "bg-yellow-400 text-black" : "bg-white/15"}`}>{children}</span>
      {label}
    </button>
  );
}

function Slider({ label, min, max, step, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-white/70 text-xs w-20 shrink-0">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="flex-1 accent-[#D97706]" />
    </div>
  );
}
